import type { Table as TableType } from '@tanstack/react-table'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components'
import type { PaginationStore } from '@/stores'
import { cn } from '@/utils'

interface TableControlsProps<TData> {
  table: TableType<TData>
  pagination: PaginationStore
  className?: string
  pageSizes?: number[]
  alwaysVisible?: boolean
}

export function TableControls<TData>({
  table,
  pagination,
  className,
  alwaysVisible = false,
  pageSizes = [100, 500, 1000],
}: TableControlsProps<TData>) {
  const pageSize = pagination.pageSize
  const pageIndex = pagination.pageIndex
  const totalRows = table.getFilteredRowModel().rows.length

  const startRow = pageIndex * pageSize + 1
  const endRow = Math.min((pageIndex + 1) * pageSize, totalRows)

  if (!alwaysVisible && pageSizes[0] > totalRows) return

  return (
    <div
      className={cn(
        'flex items-center justify-between px-2 py-3 border-t bg-background',
        className,
      )}
    >
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button theme="outline" size="sm" className="h-8 w-[70px]">
                {pageSize}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuRadioGroup
                value={String(pageSize)}
                onValueChange={(value) => table.setPageSize(Number(value))}
              >
                {pageSizes.map((size) => (
                  <DropdownMenuRadioItem key={size} value={String(size)}>
                    {size}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="text-sm text-muted-foreground">
          {totalRows === 0 ? (
            'No rows'
          ) : (
            <>
              {startRow}-{endRow} of {totalRows.toLocaleString()}
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          theme="outline"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className="h-8"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <Button
          theme="outline"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className="h-8"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
